'use client'

import { ReactNode, Suspense, useMemo } from 'react'
import { Box, Divider, Grid, styled, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  ExplorerSection,
  ExplorerTotals,
  ExplorerTraitSummary,
} from 'types/explorer'
import OverviewForm from '@components/OverviewForm'
import VerticalDivider from '@components/VerticalDivider'
import ExplorerModal from '../ExplorerModal'
import AttributesForm from '../AttributesForm'
import ExploreHeader from '../ExploreHeader'
import { usePathname, useRouter } from 'next/navigation'
import NavigationButtonGroup from '@components/NavigationButtonGroup'
import NavigationSelect from '@components/NavigationSelect'
import {
  DewiBreakpointsLg,
  DewiBreakpointsMd,
  DewiBreakpointsSm,
  DewiBreakpointsXl,
  DewiBreakpointsXs,
} from '@furry/tokens'

const LayoutRoot = styled('div')(() => ({
  position: 'relative',
  top: '100px',
}))

const Container = styled(Box)(() => ({
  paddingTop: 1,
}))

const NavLayout = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down(parseInt(DewiBreakpointsSm))]: {
    flexDirection: 'row',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  },
  [theme.breakpoints.between(
    parseInt(DewiBreakpointsSm),
    parseInt(DewiBreakpointsLg),
  )]: {
    flexDirection: 'row',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  },
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    justifyContent: 'space-between',
  },
  [theme.breakpoints.between(
    parseInt(DewiBreakpointsLg),
    parseInt(DewiBreakpointsXl),
  )]: {
    flexDirection: 'column',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  },
  [theme.breakpoints.up(parseInt(DewiBreakpointsXl))]: {
    flexDirection: 'row',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    height: '2.8rem',
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const FormGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    columnGap: 10,
  },
}))

const LargeNavGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    display: 'none',
  },
}))

const SmallNavGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up(parseInt(DewiBreakpointsLg))]: {
    display: 'none',
  },
  [theme.breakpoints.down(parseInt(DewiBreakpointsXs))]: {
    width: '35%',
  },
  [theme.breakpoints.between(
    parseInt(DewiBreakpointsXs),
    parseInt(DewiBreakpointsMd),
  )]: {
    width: '40%',
  },
  [theme.breakpoints.between(
    parseInt(DewiBreakpointsMd),
    parseInt(DewiBreakpointsLg),
  )]: {
    width: '30%',
  },
}))

type ExplorerLayoutProps = {
  children: ReactNode
  /** Headline numbers, computed on the server from the static manifest. */
  totals: ExplorerTotals
  /** Trait types and their values, for the filter drawer and trait select. */
  traits: ExplorerTraitSummary[]
}

function ExplorerLayout({ children, totals, traits }: ExplorerLayoutProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const router = useRouter()
  const pathname = usePathname()
  const navRoutes = [
    {
      name: `${t('explorerPage.navTitle.leaderboard')}` as ExplorerSection,
      isNav: true,
    },
    {
      name: `${t('explorerPage.navTitle.overview')}` as ExplorerSection,
      isNav: true,
    },
    {
      name: `${t('explorerPage.navTitle.attributes')}` as ExplorerSection,
      isNav: true,
    },
  ]

  const getActiveRoute = () => {
    const route = navRoutes.find((route) => pathname.includes(route.name))
    return route?.name || navRoutes[0].name
  }

  const activeRoute = useMemo(getActiveRoute, [pathname])

  const navDividerStyles = {
    marginRight: '1.5rem',
    [theme.breakpoints.down(1300)]: {
      display: 'none',
    },
    [theme.breakpoints.up(1300)]: {
      display: activeRoute !== 'overview' && 'none',
    },
  }

  const handleCurrentSelection = (value: string) => {
    router.push(`/explorer/${value}`)
  }
  return (
    <LayoutRoot>
      {/* ExplorerModal, OverviewForm and AttributesForm all read the query
          string, which bails the whole route out of static rendering unless
          each one sits behind its own Suspense boundary. */}
      <Suspense fallback={null}>
        <ExplorerModal traits={traits} />
      </Suspense>
      <Container>
        <ExploreHeader
          supply={totals.supply}
          legendary={totals.legendary}
          auctioned={totals.sold}
          owners={totals.owners}
          burnt={totals.burnt}
        />
        <Divider component="div" />
        <NavLayout container rowGap={3}>
          <LargeNavGrid item xs="auto">
            <NavigationButtonGroup
              routes={navRoutes}
              onClick={handleCurrentSelection}
              selected={activeRoute}
            />
          </LargeNavGrid>
          <SmallNavGrid item>
            <NavigationSelect
              routes={navRoutes}
              onSelect={handleCurrentSelection}
              value={activeRoute}
            />
          </SmallNavGrid>
          <VerticalDivider sx={navDividerStyles} />
          <FormGrid container xs rowGap={2}>
            <Suspense fallback={null}>
              {pathname.includes('overview') && <OverviewForm />}
              {pathname.includes('attributes') && (
                <AttributesForm traits={traits} />
              )}
            </Suspense>
          </FormGrid>
        </NavLayout>
        <Divider component="div" />
        {children}
      </Container>
    </LayoutRoot>
  )
}

export default ExplorerLayout
